import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  User, 
  Briefcase, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Eye,
  Filter
} from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { getGeneralQuestionnaireReviews } from 'api/apiHelper';

interface QuestionnaireSession {
  id: number;
  test_token: string;
  status: string;
  applicant_name: string;
  applicant_email: string;
  job_title: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const GeneralQuestionnaireReviewList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<QuestionnaireSession[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchQuestionnaireSessions();
  }, [statusFilter]);

  const fetchQuestionnaireSessions = async () => {
    setLoading(true);
    try {
      const response = await getGeneralQuestionnaireReviews(statusFilter);
      setSessions(response.data);
    } catch (error: any) {
      console.error('Error fetching questionnaire sessions:', error);
      toast.error('Failed to load questionnaire submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResponses = (testToken: string) => {
    navigate(`/careers/job-application/general-questionnaire-result/${testToken}`);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      completed: { 
        bg: 'bg-emerald-500/20', 
        text: 'text-emerald-400', 
        icon: <CheckCircle className="w-4 h-4" /> 
      },
      in_progress: { 
        bg: 'bg-amber-500/20', 
        text: 'text-amber-400', 
        icon: <Clock className="w-4 h-4" /> 
      },
      pending: { 
        bg: 'bg-slate-500/20', 
        text: 'text-slate-400', 
        icon: <AlertCircle className="w-4 h-4" /> 
      },
      expired: { 
        bg: 'bg-red-500/20', 
        text: 'text-red-400', 
        icon: <AlertCircle className="w-4 h-4" /> 
      },
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Backdrop message="Loading questionnaire submissions..." />;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            General Questionnaire Submissions
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Review text responses from candidates
          </p>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-white dark:bg-navy-700 border border-gray-200 dark:border-navy-600 rounded-xl text-sm text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg overflow-hidden">
        {sessions.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
              No Questionnaire Submissions
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Questionnaire submissions will appear here once candidates complete their assessments.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-navy-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-navy-700">
                {sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-navy-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {session.applicant_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {session.applicant_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {session.job_title}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(session.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(session.started_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate(session.completed_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewResponses(session.test_token)}
                        className="inline-flex items-center px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Responses
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralQuestionnaireReviewList;
